import {
  Points,
  BufferGeometry,
  BufferAttribute,
  ShaderMaterial,
  AdditiveBlending,
  NormalBlending,
  Color,
  Vector3,
} from 'three';

export type ParticleMode = 'none' | 'rain' | 'snow' | 'sparks' | 'dust' | 'leaves';

interface ModeDef {
  count: number;
  color: number;
  size: number;
  opacity: number;
  additive: boolean;
  vel: () => [number, number, number]; // базовая скорость частицы
  beatBoost: number; // доп. вклад удара в размер/яркость
}

const AREA = 34; // полуразмер коробки по X/Z
const Y0 = -5;
const Y1 = 50;
const SPAN = Y1 - Y0;
const rand = (a: number, b: number): number => a + Math.random() * (b - a);

const MODES: Record<Exclude<ParticleMode, 'none'>, ModeDef> = {
  rain: { count: 1300, color: 0xbfd4ff, size: 4, opacity: 0.5, additive: false, beatBoost: 0, vel: () => [rand(-1, 1), rand(-34, -26), rand(-1, 1)] },
  snow: { count: 700, color: 0xffffff, size: 9, opacity: 0.85, additive: false, beatBoost: 0.2, vel: () => [rand(-1.2, 1.2), rand(-3.5, -1.8), rand(-1.2, 1.2)] },
  sparks: { count: 500, color: 0xffd27a, size: 7, opacity: 0.9, additive: true, beatBoost: 1.4, vel: () => [rand(-1.5, 1.5), rand(2, 6), rand(-1.5, 1.5)] },
  dust: { count: 450, color: 0xeaf0ff, size: 6, opacity: 0.5, additive: true, beatBoost: 0.6, vel: () => [rand(-0.6, 0.6), rand(-0.3, 0.6), rand(-0.6, 0.6)] },
  leaves: { count: 350, color: 0xc7e08a, size: 12, opacity: 0.9, additive: false, beatBoost: 0.3, vel: () => [rand(2, 7), rand(-2.5, -0.5), rand(-1.5, 1.5)] },
};

const VERT = /* glsl */ `
attribute float aSeed;
uniform float uSize;
varying float vSeed;
void main(){
  vSeed = aSeed;
  vec4 mv = modelViewMatrix * vec4(position, 1.0);
  gl_Position = projectionMatrix * mv;
  gl_PointSize = uSize * 300.0 / max(0.1, -mv.z);
}
`;
const FRAG = /* glsl */ `
uniform vec3 uColor;
uniform float uOpacity;
varying float vSeed;
void main(){
  vec2 uv = gl_PointCoord * 2.0 - 1.0;
  float d = dot(uv, uv);
  if (d > 1.0) discard;
  float a = smoothstep(1.0, 0.0, d) * uOpacity;
  gl_FragColor = vec4(uColor, a);
}
`;

/**
 * Лёгкий Points-слой частиц погоды (дождь/снег/искры/пыль/листья). Тороидальная
 * коробка вокруг спирали; скорость+ветер двигают, бит подкручивает размер/яркость.
 */
export class Particles {
  readonly points: Points;
  private geo: BufferGeometry;
  private mat: ShaderMaterial;
  private pos: Float32Array;
  private vel: Float32Array;
  private max: number;
  private active = 0;
  private mode: ParticleMode = 'none';
  private wind = new Vector3();
  private baseSize = 5;
  private densityMult = 1; // дев-панель: множитель числа частиц

  constructor(max = 1400) {
    this.max = max;
    this.pos = new Float32Array(max * 3);
    this.vel = new Float32Array(max * 3);
    const seed = new Float32Array(max);
    for (let i = 0; i < max; i++) {
      this.pos[i * 3] = rand(-AREA, AREA);
      this.pos[i * 3 + 1] = rand(Y0, Y1);
      this.pos[i * 3 + 2] = rand(-AREA, AREA);
      seed[i] = Math.random();
    }
    this.geo = new BufferGeometry();
    this.geo.setAttribute('position', new BufferAttribute(this.pos, 3));
    this.geo.setAttribute('aSeed', new BufferAttribute(seed, 1));
    this.geo.setDrawRange(0, 0);
    this.mat = new ShaderMaterial({
      uniforms: { uColor: { value: new Color(0xffffff) }, uSize: { value: 5 }, uOpacity: { value: 0.6 } },
      vertexShader: VERT,
      fragmentShader: FRAG,
      transparent: true,
      depthWrite: false,
    });
    this.points = new Points(this.geo, this.mat);
    this.points.frustumCulled = false;
    this.points.renderOrder = 3;
  }

  setWind(x: number, z: number): void {
    this.wind.set(x, 0, z);
  }

  /** Дев-панель: множитель плотности частиц (0..1.5), вживую (без ребилда буфера). */
  setDensity(m: number): void {
    this.densityMult = Math.max(0, Math.min(1.5, m));
    if (this.mode !== 'none') this.setMode(this.mode); // пересчитать active + скорости
  }

  setMode(mode: ParticleMode): void {
    this.mode = mode;
    if (mode === 'none') {
      this.active = 0;
      this.geo.setDrawRange(0, 0);
      return;
    }
    const d = MODES[mode];
    this.active = Math.min(Math.floor(d.count * this.densityMult), this.max);
    this.baseSize = d.size;
    (this.mat.uniforms.uColor.value as Color).setHex(d.color);
    this.mat.uniforms.uSize.value = d.size;
    this.mat.uniforms.uOpacity.value = d.opacity;
    this.mat.blending = d.additive ? AdditiveBlending : NormalBlending;
    this.mat.needsUpdate = true;
    for (let i = 0; i < this.active; i++) {
      const v = d.vel();
      this.vel[i * 3] = v[0];
      this.vel[i * 3 + 1] = v[1];
      this.vel[i * 3 + 2] = v[2];
    }
    this.geo.setDrawRange(0, this.active);
  }

  update(dt: number, beat = 0): void {
    if (this.mode === 'none' || this.active === 0) return;
    const p = this.pos;
    const v = this.vel;
    const wx = this.wind.x;
    const wz = this.wind.z;
    for (let i = 0; i < this.active; i++) {
      const j = i * 3;
      p[j] += (v[j] + wx) * dt;
      p[j + 1] += v[j + 1] * dt;
      p[j + 2] += (v[j + 2] + wz) * dt;
      // тороидальный wrap в коробке
      if (p[j] > AREA) p[j] -= 2 * AREA;
      else if (p[j] < -AREA) p[j] += 2 * AREA;
      if (p[j + 2] > AREA) p[j + 2] -= 2 * AREA;
      else if (p[j + 2] < -AREA) p[j + 2] += 2 * AREA;
      if (p[j + 1] > Y1) p[j + 1] -= SPAN;
      else if (p[j + 1] < Y0) p[j + 1] += SPAN;
    }
    (this.geo.attributes.position as BufferAttribute).needsUpdate = true;
    const d = MODES[this.mode as Exclude<ParticleMode, 'none'>];
    this.mat.uniforms.uSize.value = this.baseSize * (1 + beat * d.beatBoost);
  }

  dispose(): void {
    this.geo.dispose();
    this.mat.dispose();
  }
}
